import React from 'react';
import { motion } from 'framer-motion';
import { StarIcon } from 'lucide-react';
import { SectionLabel } from './SectionLabel';

/* 2026-09-17 (Kevin): two real Google reviews, typed out from the Google
   Business Profile rather than screenshotted so the text is selectable,
   readable on mobile, and indexable. Copy is verbatim from Google; only
   the star count and reviewer name travel with it. Purely informational,
   no solicitation and no gating, per the sitewide rule. */

const reviews = [{
  name: 'Theresa Fendrick',
  initial: 'T',
  accent: 'bg-violet-500',
  when: '23 weeks ago',
  services: ['Website Design'],
  body: 'I cannot say enough how amazing Kevin is to work with! I am a new business owner and the experience with Kevin put starting my first business at ease. I loved how he took ownership of the project and added his creative ideas to make my website. In addition, Kevin moves quick with efficiency. I will definitely be working with him again!'
}, {
  name: 'Isaias Torres',
  initial: 'I',
  accent: 'bg-teal-500',
  when: 'Recently',
  services: ['AI Reception', 'Website Design', 'Yelp Ads Management'],
  body: 'Friendly, very nice work with Kevin, he is a professional person to help you and anything you need a easy work with him I glad find him'
}];
function ServiceTags({
  services
}: {
  services: string[];
}) {
  return <ul className="flex flex-wrap items-center gap-2" data-id="element-667" data-mp="3a3omny3m4a49">
      {services.map(s => <li key={s} className="rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.12em] text-emerald-400" data-id="element-668" data-mp="354c3bnby9l6f">
          {s}
        </li>)}
    </ul>;
}
function Stars() {
  return <span className="flex items-center gap-0.5" aria-label="Rated 5 out of 5 stars" data-id="element-669" data-mp="2w9xmhgbru2l7">
      {[0, 1, 2, 3, 4].map(i => <StarIcon key={i} className="h-4 w-4 fill-amber-400 text-amber-400" aria-hidden="true" data-id="element-670" data-mp="3o2ekxh264fu7" />)}
    </span>;
}
export function Reviews() {
  const [featured, ...rest] = reviews;
  return <section className="border-b border-[#1a1a1a] bg-[#0c0c0c]" data-id="element-671" data-mp="1r50in7qddvn9">
      <div className="mx-auto w-full max-w-6xl px-6 py-20 lg:py-24" data-id="element-672" data-mp="1r6k1n63euksq">
        <SectionLabel data-id="element-673" data-mp="1cf1mtbmaeway">What owners say</SectionLabel>
        <h2 className="mt-8 max-w-2xl text-3xl font-bold leading-tight tracking-tight text-white sm:text-4xl" data-id="element-674" data-mp="1hee83rxifvbf">
          Reviews from the people who hired us.
        </h2>

        <div className="mt-12 grid grid-cols-1 gap-6 lg:grid-cols-[1.4fr_1fr]" data-id="element-675" data-mp="13gzfyffvh9fr">
          <motion.figure initial={{
          opacity: 0,
          y: 12
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-80px'
        }} transition={{
          duration: 0.4
        }} className="flex flex-col rounded-2xl border border-[#1c1c1c] bg-[#0d0d0d] p-8" data-id="element-676" data-mp="18fkco4vx1azg">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-3" data-id="element-677" data-mp="2nsnd503vv3y2">
              <Stars data-id="element-678" data-mp="2stb0nbswl2nj" />
              <ServiceTags services={featured.services} data-id="element-679" data-mp="2srqdi78x4lzp" />
            </div>
            <blockquote className="mt-6 text-xl leading-relaxed text-neutral-200" data-id="element-680" data-mp="h70pj37851ex">
              &ldquo;{featured.body}&rdquo;
            </blockquote>
            <figcaption className="mt-auto flex items-center gap-3 pt-8" data-id="element-681" data-mp="qw0ud38eqr11">
              <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-semibold text-white ${featured.accent}`} aria-hidden="true" data-id="element-682" data-mp="qy3sybnmlshd">
                {featured.initial}
              </span>
              <span className="text-sm" data-id="element-683" data-mp="vuu9t1798s3u">
                <span className="block font-medium text-white" data-id="element-684" data-mp="19uwlg5oyypan">{featured.name}</span>
                <span className="block text-neutral-500" data-id="element-685" data-mp="19ssj3s6pxych">
                  Google review &middot; {featured.when}
                </span>
              </span>
            </figcaption>
          </motion.figure>

          <div className="flex flex-col gap-6" data-id="element-686" data-mp="36next1363u9e">
            {rest.map((r, i) => <motion.figure key={r.name} initial={{
            opacity: 0,
            y: 12
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-80px'
          }} transition={{
            duration: 0.4,
            delay: 0.08 * (i + 1)
          }} className="flex flex-1 flex-col rounded-2xl border border-[#1c1c1c] bg-[#0d0d0d] p-7" data-id="element-687" data-mp="36prmdx3fl2vf" data-mpi={i}>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-3" data-id="element-688" data-mp="3mzibtrsktei4">
                  <Stars data-id="element-689" data-mp="2pfxeshj5y1nz" />
                  <ServiceTags services={r.services} data-id="element-690" data-mp="2ud75s1m7aldx" />
                </div>
                <blockquote className="mt-5 text-base leading-relaxed text-neutral-300" data-id="element-691" data-mp="2ub329ks7roe6">
                  &ldquo;{r.body}&rdquo;
                </blockquote>
                <figcaption className="mt-auto flex items-center gap-3 pt-7" data-id="element-692" data-mp="34421rxp90zck">
                  <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-semibold text-white ${r.accent}`} aria-hidden="true" data-id="element-693" data-mp="26o4o4trkgsjf">
                    {r.initial}
                  </span>
                  <span className="text-sm" data-id="element-694" data-mp="opzwvmutlga0">
                    <span className="block font-medium text-white" data-id="element-695" data-mp="orb2ztbqn04a">{r.name}</span>
                    <span className="block text-neutral-500" data-id="element-696" data-mp="jq3tnpty5svi">Google review &middot; {r.when}</span>
                  </span>
                </figcaption>
              </motion.figure>)}
          </div>
        </div>
      </div>
    </section>;
}